export type Stage = 'SUPERVISOR' | 'ACCOUNTING' | 'ADMINISTRATION' | 'PRESIDENT';

export const STAGES: Stage[] = ['SUPERVISOR', 'ADMINISTRATION', 'ACCOUNTING', 'PRESIDENT'];
export const STAGE_LABEL: Record<Stage, string> = {
  SUPERVISOR: 'Supervisor',
  ACCOUNTING: 'Accounting',
  ADMINISTRATION: 'Administration',
  PRESIDENT: 'Final approval',
};

export interface StageInfo {
  stage: Stage;
  decision: 'Y' | 'N' | null;
  byName: string | null;
  date: string | null;
  at: string | null;
  note: string | null;
}

export interface BonusSummary {
  id: string;
  categoryId: string;
  categoryName: string;
  frequency: 'WEEKLY' | 'MONTHLY' | 'ONE_TIME' | 'PER_EVENT';
  periodStart: string;
  periodEnd: string;
  campaignId: string | null;
  campaignName: string;
  offeredBy: 'CUSTOMER' | 'IN_HOUSE';
  currency: string;
  concept: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  nextStage: Stage | null;
  /** The three first approvals are done and the final approval is still pending. */
  awaitingFinal?: boolean;
  stages: StageInfo[];
  customer: { approved: string | null; approvedDate: string | null; paid: string | null; paidDate: string | null };
  employeeCount: number;
  total: number;
  includedInPayroll: boolean;
  allIncluded: boolean;
  hrSynced: boolean;
  createdByName: string | null;
  readyForPayroll: boolean;
}

export interface BonusLine {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string | null;
  amount: number;
  concept: string | null;
  status: 'ACTIVE' | 'REMOVED';
  removedByName: string | null;
  removedAt: string | null;
  removedReason: string | null;
  addedByName: string | null;
  includedInPayroll: boolean;
  payrollRef: string | null;
  hrSyncedAt: string | null;
}

export interface BonusAttachment {
  id: string;
  kind: 'PROOF' | 'SCREENSHOT';
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedByName: string | null;
  createdAt: string;
}

export interface BonusEvent {
  lineId: string | null;
  action: string;
  stage: string | null;
  decision: string | null;
  actorName: string | null;
  actorRole: string | null;
  override: number;
  note: string | null;
  createdAt: string;
}

export interface BonusPermissions {
  canEdit: boolean;
  canAttach?: boolean;
  readOnlyReason: string | null;
  editClearsApprovals: boolean;
  canRemoveEmployee: boolean;
  actionableStage: Stage | null;
  /** The newest decision, which this user may cancel (approvals are cancelled newest first). */
  cancellableStage?: Stage | null;
  cancelBlockedReason?: string | null;
  blockedReason: string | null;
  canDelete: boolean;
}

export interface BonusDetail extends BonusSummary {
  lines: BonusLine[];
  events: BonusEvent[];
  attachments?: BonusAttachment[];
  permissions?: BonusPermissions;
}

export interface BonusCategory {
  id: string;
  name: string;
  frequency: string;
  offeredBy: 'CUSTOMER' | 'IN_HOUSE';
  defaultAmount: number | null;
  currency: string;
  isSystem: number;
  active?: number;
}

export interface Campaign { id: string; name: string }
export interface EmployeeOption { id: string; firstName: string; lastName: string; employeeNumber?: string | null }

export const errText = (e: any): string => e?.response?.data?.message || e?.message || 'Request failed';
export const money = (n: number, currency = 'USD') =>
  `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)} ${currency}`;
export const todayYmd = () => new Date().toISOString().slice(0, 10);
export const employeeLabel = (u: EmployeeOption) => `${u.firstName} ${u.lastName}${u.employeeNumber ? ` (${u.employeeNumber})` : ''}`;
